import { formatDateInputValue } from '../../../shared/utils/localization';
import { jsPDF } from 'jspdf';

import { CONSULTATION_PDF_FONT_FAMILY, applyConsultationPdfFonts } from '../../consultations/utils/consultationPdfFont';
import {
  type CalendarEventsPdfFieldKey,
  type CalendarEventsPdfTheme,
  DEFAULT_CALENDAR_EVENTS_PDF_THEME,
  hexToRgb,
} from './calendarEventsPdfTheme';

export type CalendarEventExportRow = {
  start: string;
  end: string;
  title: string;
  status: string;
  room: string;
  staff: string;
  contact: string;
  notes: string;
};

export type CalendarEventsExportLabels = {
  reportTitle: string;
  exportedAt: string;
  dateRange: string;
  roomGroup: string;
  filters: string;
  eventCount: string;
  title: string;
  when: string;
  start: string;
  end: string;
  status: string;
  room: string;
  staff: string;
  contact: string;
  notes: string;
  noEvents: string;
  page: string;
  of: string;
};

export type CalendarEventsExportInput = {
  rows: CalendarEventExportRow[];
  labels: CalendarEventsExportLabels;
  dateRangeLabel: string;
  roomGroupLabel: string;
  filterSummary: string;
  theme?: CalendarEventsPdfTheme;
  exportedAt?: Date;
};

type PdfColumn = {
  key: CalendarEventsPdfFieldKey | 'index';
  label: string;
  width: number;
  fieldKey?: CalendarEventsPdfFieldKey;
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const setPdfTextColor = (doc: jsPDF, hex: string) => {
  const [r, g, b] = hexToRgb(hex);
  doc.setTextColor(r, g, b);
};

const setPdfFillColor = (doc: jsPDF, hex: string) => {
  const [r, g, b] = hexToRgb(hex);
  doc.setFillColor(r, g, b);
};

const setPdfDrawColor = (doc: jsPDF, hex: string) => {
  const [r, g, b] = hexToRgb(hex);
  doc.setDrawColor(r, g, b);
};

const getPageMetrics = (doc: jsPDF) => ({
  width: doc.internal.pageSize.getWidth(),
  height: doc.internal.pageSize.getHeight(),
});

const addPageNumbers = (
  doc: jsPDF,
  labels: CalendarEventsExportLabels,
  bottomMargin: number,
) => {
  const pageCount = doc.getNumberOfPages();
  const { width, height } = getPageMetrics(doc);

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setFontSize(8);
    setPdfTextColor(doc, '#64748b');
    doc.text(
      `${labels.page} ${page} ${labels.of} ${pageCount}`,
      width / 2,
      height - bottomMargin / 2,
      { align: 'center' },
    );
  }
};

const drawWrappedCellText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  lineHeight: number,
  maxLines: number,
) => {
  const lines = doc.splitTextToSize(text || '-', Math.max(width - 2, 8));
  const visibleLines = lines.slice(0, maxLines);
  let currentY = y;

  visibleLines.forEach((line: string) => {
    doc.text(String(line), x + 1.5, currentY);
    currentY += lineHeight;
  });

  return visibleLines.length * lineHeight;
};

export const sanitizeCalendarEventsExportFileName = (exportedAt: Date) => {
  const stamp = formatDateInputValue(exportedAt);
  return `calendar-events-${stamp}`;
};

export const exportCalendarEventsToPdf = async (input: CalendarEventsExportInput) => {
  const labels = input.labels;
  const theme = input.theme || DEFAULT_CALENDAR_EVENTS_PDF_THEME;
  const exportedAt = input.exportedAt || new Date();
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  await applyConsultationPdfFonts(doc);

  const left = 10;
  const right = 10;
  const top = 10;
  const bottomMargin = 12;
  const { width: pageWidth, height: pageHeight } = getPageMetrics(doc);
  const contentWidth = pageWidth - left - right;
  const lineHeight = 3.8;
  const cellPaddingY = 2.2;

  const allColumns: PdfColumn[] = [
    { key: 'index', label: '#', width: 7 },
    { key: 'time', label: labels.when, width: 36, fieldKey: 'time' },
    { key: 'title', label: labels.title, width: 34, fieldKey: 'title' },
    { key: 'status', label: labels.status, width: 22, fieldKey: 'status' },
    { key: 'room', label: labels.room, width: 24, fieldKey: 'room' },
    { key: 'staff', label: labels.staff, width: 28, fieldKey: 'staff' },
    { key: 'contact', label: labels.contact, width: 28, fieldKey: 'contact' },
    { key: 'notes', label: labels.notes, width: 0, fieldKey: 'notes' },
  ];

  const columns = allColumns.filter((column) => (
    column.key === 'index'
    || (column.fieldKey ? theme.columnVisibility[column.fieldKey] !== false : true)
  ));

  if (columns.length === 1) {
    columns.push(...allColumns.filter((column) => column.key !== 'index'));
  }

  columns[columns.length - 1].width = contentWidth - columns.slice(0, -1).reduce((sum, column) => sum + column.width, 0);

  const drawHeaderBand = (y: number) => {
    setPdfFillColor(doc, theme.headerBackground);
    doc.roundedRect(left, y, contentWidth, 14, 2, 2, 'F');

    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(14);
    setPdfTextColor(doc, theme.headerText);
    doc.text(labels.reportTitle, left + 4, y + 6.5);

    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setFontSize(8.5);
    doc.text(`${labels.exportedAt}: ${exportedAt.toLocaleString()}`, left + 4, y + 11.5);

    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(10);
    const countLabel = `${labels.eventCount}: ${input.rows.length}`;
    doc.text(countLabel, left + contentWidth - 4, y + 8.5, { align: 'right' });
  };

  const drawSummaryCard = (startY: number) => {
    const cardHeight = input.filterSummary.trim() ? 20 : 16;
    setPdfDrawColor(doc, theme.borderColor);
    setPdfFillColor(doc, '#ffffff');
    doc.roundedRect(left, startY, contentWidth, cardHeight, 2, 2, 'FD');

    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(8);
    setPdfTextColor(doc, '#64748b');

    const labelX = left + 4;
    const valueX = left + 34;
    let y = startY + 5.5;

    doc.text(`${labels.dateRange}:`, labelX, y);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    setPdfTextColor(doc, '#0f172a');
    doc.text(input.dateRangeLabel, valueX, y);

    y += 5;
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    setPdfTextColor(doc, '#64748b');
    doc.text(`${labels.roomGroup}:`, labelX, y);
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    setPdfTextColor(doc, '#0f172a');
    doc.text(input.roomGroupLabel, valueX, y);

    if (input.filterSummary.trim()) {
      y += 5;
      doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
      setPdfTextColor(doc, '#64748b');
      doc.text(`${labels.filters}:`, labelX, y);
      doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
      setPdfTextColor(doc, '#0f172a');
      const filterLines = doc.splitTextToSize(input.filterSummary, contentWidth - 40);
      doc.text(filterLines.slice(0, 2), valueX, y);
    }

    return startY + cardHeight + 4;
  };

  const drawTableHeader = (y: number) => {
    const headerHeight = 7;
    setPdfFillColor(doc, theme.tableHeaderBackground);
    doc.rect(left, y, contentWidth, headerHeight, 'F');

    setPdfDrawColor(doc, theme.borderColor);
    doc.rect(left, y, contentWidth, headerHeight);

    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'bold');
    doc.setFontSize(7.5);
    setPdfTextColor(doc, theme.tableHeaderText);

    let x = left;
    columns.forEach((column) => {
      doc.text(column.label, x + 1.5, y + 4.8);
      x += column.width;
    });

    return y + headerHeight;
  };

  const getRowValue = (row: CalendarEventExportRow, column: PdfColumn, index: number) => {
    if (column.key === 'index') {
      return String(index + 1);
    }

    if (column.key === 'time') {
      return `${row.start}\n${row.end}`;
    }

    return row[column.key as keyof CalendarEventExportRow] || '-';
  };

  let y = top;
  drawHeaderBand(y);
  y += 18;
  y = drawSummaryCard(y);

  if (input.rows.length === 0) {
    doc.setFont(CONSULTATION_PDF_FONT_FAMILY, 'normal');
    doc.setFontSize(10);
    setPdfTextColor(doc, '#64748b');
    doc.text(labels.noEvents, left + 4, y + 8);
    addPageNumbers(doc, labels, bottomMargin);
    const fileName = `${sanitizeCalendarEventsExportFileName(exportedAt)}.pdf`;
    triggerBlobDownload(doc.output('blob'), fileName);
    return;
  }

  y = drawTableHeader(y);

  input.rows.forEach((row, rowIndex) => {
    const notes = row.notes.trim();
    const timeLines = 2;
    const baseLines = Math.max(timeLines, notes ? 2 : 1);
    const rowHeight = cellPaddingY * 2 + baseLines * lineHeight + (notes ? lineHeight * 1.5 : 0);

    if (y + rowHeight > pageHeight - bottomMargin) {
      doc.addPage();
      y = top;
      y = drawTableHeader(y);
    }

    if (rowIndex % 2 === 1) {
      setPdfFillColor(doc, theme.rowAlternateBackground);
      doc.rect(left, y, contentWidth, rowHeight, 'F');
    }

    setPdfDrawColor(doc, theme.borderColor);
    doc.rect(left, y, contentWidth, rowHeight);

    let x = left;
    columns.forEach((column) => {
      doc.line(x, y, x, y + rowHeight);

      const fieldKey = column.fieldKey;
      const color = fieldKey
        ? theme.fields[fieldKey]
        : '#334155';

      doc.setFont(
        CONSULTATION_PDF_FONT_FAMILY,
        column.key === 'title' || column.key === 'index' ? 'bold' : 'normal',
      );
      doc.setFontSize(column.key === 'notes' ? 7 : 7.5);
      setPdfTextColor(doc, color);

      const value = getRowValue(row, column, rowIndex);
      const maxLines = column.key === 'time' ? 2 : column.key === 'notes' && notes ? 3 : 2;
      drawWrappedCellText(
        doc,
        value,
        x,
        y + cellPaddingY + lineHeight,
        column.width,
        lineHeight,
        maxLines,
      );

      x += column.width;
    });

    doc.line(left + contentWidth, y, left + contentWidth, y + rowHeight);
    y += rowHeight;
  });

  addPageNumbers(doc, labels, bottomMargin);

  const fileName = `${sanitizeCalendarEventsExportFileName(exportedAt)}.pdf`;
  triggerBlobDownload(doc.output('blob'), fileName);
};
